import React, { useState } from 'react';

import map from 'lodash/map';
import sumBy from 'lodash/sumBy';
import isNil from 'lodash/isNil';
import invert from 'lodash/invert';
import find from 'lodash/find';
import get from 'lodash/get';

import { Box, Dialog, Typography, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BackpackIcon from '@mui/icons-material/Backpack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { PlainObject, EventFormData } from '../components/data_types/types';
import { HOLE_PREFIX_TO_DATA, HOLE_TO_SCENE } from '../components/data/holes';
import { DISC_BAG_NAMES } from '../components/data/disc_data';
import { formatEventDate } from '../components/data/formatting';
import { DS, Overline, numericSx, formatSigned } from '../components/shared/Dashboard';

interface Props {
    round: PlainObject | null;
    event: EventFormData | PlainObject | null;
    isOpen: boolean;
    onClose: ()=> void;
};

interface LayoutHole {
    holeName: string;
    holeNum: number;
    courseShortname: string;
    displayName: string;
    par: number;
    elite: number;
}

function getDiscBag(bagVal: number) {
    return get(find(DISC_BAG_NAMES, ['value', bagVal]), 'label', 'Default');
}

// Under par is good in golf, so elite below par reads as green
const diffColor = (diff: number) => diff < 0 ? DS.gain : diff > 0 ? DS.loss : DS.neutral;

const paperProps = {
    sx: {
        background: DS.surface,
        backgroundImage: 'none',
        border: `1px solid ${DS.border}`,
        borderRadius: DS.radius,
        m: { xs: 1.5, sm: 4 },
        width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
    },
};

const Tag: React.FC<{ color: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ color, icon, children }) => (
    <Box component="span" sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: '3px',
        borderRadius: '999px',
        background: alpha(color, 0.14),
        color,
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        '& svg': { fontSize: 13 },
    }}>
        {icon}{children}
    </Box>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
    <Box sx={{ minWidth: 0 }}>
        <Overline>{label}</Overline>
        <Typography sx={{ fontSize: '24px', fontWeight: 800, lineHeight: 1.2, color: color ?? DS.text, ...numericSx }}>
            {value}
        </Typography>
    </Box>
);

const CopyCode: React.FC<{ code: string }> = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (error) {
            console.error('Could not copy event code:', error);
        }
    };

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy event code'}>
            <Box component="button" onClick={handleCopy} sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: '2px',
                border: `1px solid ${DS.border}`,
                borderRadius: '8px',
                background: DS.inset,
                color: DS.text,
                fontFamily: 'monospace',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { borderColor: DS.borderStrong },
            }}>
                {code}
                {copied ? <CheckIcon sx={{ fontSize: 13, color: DS.gain }} /> : <ContentCopyIcon sx={{ fontSize: 13, color: DS.textMuted }} />}
            </Box>
        </Tooltip>
    );
};

const Header: React.FC<{ title: string; overline: string; tags?: React.ReactNode; onClose: () => void; icon?: React.ReactNode }> = ({ title, overline, tags, onClose, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 2, borderBottom: `1px solid ${DS.border}` }}>
        <Box sx={{ display: 'flex', p: 1, borderRadius: '12px', background: alpha(DS.brand, 0.14), color: DS.brand, flexShrink: 0 }}>
            {icon ?? <GolfCourseIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Overline>{overline}</Overline>
            <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, letterSpacing: '-0.02em', color: DS.text, lineHeight: 1.2 }}>
                {title}
            </Typography>
            {tags && <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>{tags}</Box>}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small" sx={{ color: DS.textMuted, border: `1px solid ${DS.border}`, '&:hover': { color: DS.text, background: DS.inset } }}>
            <CloseIcon fontSize="small" />
        </IconButton>
    </Box>
);

const RoundPreviewModal = ({ round, event, isOpen, onClose }: Props)=> {

    if (isNil(round)) {
        return null;
    }

    // Get round index from event if available
    const roundIndex = event?.rounds ? event.rounds.findIndex((r: any) => r === round) + 1 : 1;
    const overline = `${event?.eventName ? `${event.eventName} · ` : ''}Round ${roundIndex}`;

    // Handle cases where round.holes might be undefined or empty
    if (!round.holes || !Array.isArray(round.holes) || round.holes.length === 0) {
        return (
            <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth PaperProps={paperProps}>
                <Header
                    title="Missing Round Data"
                    overline={`${overline} · ${round.courseName || 'Unknown Course'}`}
                    icon={<WarningAmberIcon />}
                    onClose={onClose}
                />
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography sx={{ fontSize: '14px', color: DS.text, mb: 1 }}>
                        This round has no hole data available. This could be due to:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, color: DS.textMuted, fontSize: '13px', '& li': { mb: 0.5 } }}>
                        <li>The round is from an archived event with incomplete data</li>
                        <li>Data corruption or migration issues</li>
                        <li>The round was created before hole data was tracked</li>
                    </Box>
                </Box>
            </Dialog>
        );
    }

    const SCENE_TO_HOLE = invert(HOLE_TO_SCENE);

    // Track data issues for user notification
    const dataIssues: string[] = [];

    const layout: LayoutHole[] = map(round.holes, (hole)=> {
        const holeName = SCENE_TO_HOLE[hole.holeName];

        // Handle cases where holeName might be undefined
        if (!holeName) {
            dataIssues.push(`Unknown hole mapping for: ${hole.holeName || 'N/A'}`);
            return {
                holeName: hole.holeName || 'Unknown',
                holeNum: 0,
                courseShortname: 'unknown',
                displayName: `Unknown Hole (${hole.holeName || 'N/A'})`,
                par: 3, // Default par
                elite: 3, // Default elite score
            };
        }

        const holeSplit = holeName.split(/(\d+)/);
        // Use the LAST digit group as the hole number so that keys like
        // "MHG_2024_1" (where "2024" is the year, not the hole) are parsed
        // correctly. Everything before the last digit group, with underscores
        // stripped, becomes the course shortname (e.g. "MHG_2024_" → "MHG2024").
        const holeNum = parseInt(holeSplit[holeSplit.length - 2]) || 0;
        const courseShortname = holeSplit.slice(0, -2).join('').replace(/_/g, '');
        const course = HOLE_PREFIX_TO_DATA[courseShortname];

        // Handle cases where course data is not found
        if (!course) {
            dataIssues.push(`Missing course data for: ${courseShortname}`);
            return {
                holeName,
                holeNum,
                courseShortname,
                displayName: `${courseShortname} ${holeNum} (Course data not found)`,
                par: 3, // Default par
                elite: 3, // Default elite score
            };
        }

        const courseName = course.name;

        // Check for missing par/elite data
        const par = course.par[holeNum - 1];
        const elite = course.elite[holeNum - 1];

        if (par === undefined) {
            dataIssues.push(`Missing par data for ${courseName} hole ${holeNum}`);
        }
        if (elite === undefined) {
            dataIssues.push(`Missing elite data for ${courseName} hole ${holeNum}`);
        }

        return {
            holeName,
            holeNum,
            courseShortname,
            displayName: `${courseName} ${holeNum}`,
            par: par || 3, // Default to par 3 if not found
            elite: elite || 3, // Default to elite 3 if not found
        };
    });

    const totalPar = sumBy(layout, (hole) => (hole.par || 0));
    const totalElite = sumBy(layout, (hole) => (hole.elite || 0));
    // Calculate elite layout score with safer handling
    const eliteLayoutScore = totalElite - totalPar;

    const hasDates = !!(event?.eventStartTime || event?.eventEndTime);

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth scroll="body" PaperProps={paperProps}>
            <Header
                title={round.courseName || 'Unknown Course'}
                overline={overline}
                onClose={onClose}
                tags={
                    <>
                        {round.discBag !== undefined && (
                            <Tag color={DS.info} icon={<BackpackIcon />}>{getDiscBag(round.discBag)}</Tag>
                        )}
                        {round.locked !== undefined && (
                            <Tag color={round.locked ? DS.warning : DS.gain} icon={round.locked ? <LockIcon /> : <LockOpenIcon />}>
                                {round.locked ? 'Locked' : 'Open'}
                            </Tag>
                        )}
                        {round.customCourse && <Tag color={DS.neutral}>Custom course</Tag>}
                    </>
                }
            />

            <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Key numbers */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                    gap: 2,
                    p: 2,
                    borderRadius: '12px',
                    background: DS.inset,
                }}>
                    <Stat label="Holes" value={layout.length} />
                    <Stat label="Par" value={totalPar} />
                    <Stat label="Elite" value={totalElite} />
                    <Stat label="Elite layout score" value={eliteLayoutScore === 0 ? 'E' : formatSigned(eliteLayoutScore)} color={diffColor(eliteLayoutScore)} />
                </Box>

                {/* Event code + dates */}
                {(event?.eventID || hasDates) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 3 }, flexWrap: 'wrap', fontSize: '13px' }}>
                        {event?.eventID && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>Code</Typography>
                                <CopyCode code={event.eventID} />
                            </Box>
                        )}
                        {event?.eventStartTime && event.eventStartTime !== '' && (
                            <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>
                                Start <Box component="span" sx={{ color: DS.text, fontWeight: 600 }}>{formatEventDate(event.eventStartTime)}</Box>
                            </Typography>
                        )}
                        {event?.eventEndTime && event.eventEndTime !== '' && (
                            <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>
                                End <Box component="span" sx={{ color: DS.text, fontWeight: 600 }}>{formatEventDate(event.eventEndTime)}</Box>
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Data Issues */}
                {dataIssues.length > 0 && (
                    <Box sx={{ p: 1.5, borderRadius: '12px', background: alpha(DS.warning, 0.08), border: `1px solid ${alpha(DS.warning, 0.3)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <WarningAmberIcon sx={{ fontSize: 18, color: DS.warning }} />
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: DS.warning }}>Data Issues Detected</Typography>
                        </Box>
                        <Box component="ul" sx={{ m: 0, pl: 3.5, color: DS.textMuted, fontSize: '13px' }}>
                            {dataIssues.map((issue, index) => (
                                <li key={index}>{issue}</li>
                            ))}
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: DS.textFaint, mt: 0.75, pl: 3.5 }}>
                            Default values have been used where data is missing.
                        </Typography>
                    </Box>
                )}

                {/* Hole layout */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.25 }}>
                        <Overline>Hole layout</Overline>
                        <Typography sx={{ fontSize: '12px', color: DS.textFaint }}>Par · Elite · Diff</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.25 }}>
                        {map(layout, (hole, idx) => {
                            const diff = hole.elite - hole.par;
                            return (
                                <Box key={idx} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    p: 1.25,
                                    borderRadius: '12px',
                                    background: DS.inset,
                                    border: `1px solid ${DS.border}`,
                                }}>
                                    <Box sx={{
                                        width: 30,
                                        height: 30,
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        background: alpha(DS.brand, 0.14),
                                        color: DS.brand,
                                        fontSize: '13px',
                                        fontWeight: 800,
                                        ...numericSx,
                                    }}>
                                        {idx + 1}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography noWrap title={hole.displayName} sx={{ fontSize: '14px', fontWeight: 700, color: DS.text }}>
                                            {hole.displayName}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>
                                            Par {hole.par} · Elite {hole.elite}
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        minWidth: 32,
                                        textAlign: 'center',
                                        px: 0.75,
                                        py: '2px',
                                        borderRadius: '999px',
                                        background: alpha(diffColor(diff), 0.14),
                                        color: diffColor(diff),
                                        fontSize: '12px',
                                        fontWeight: 800,
                                        ...numericSx,
                                    }}>
                                        {diff === 0 ? 'E' : formatSigned(diff)}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default RoundPreviewModal;
